import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerAndroidScanningLibrary,
  CapacitorBarcodeScannerScanOrientation,
} from '@capacitor/barcode-scanner';
import { Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useLazyGetPublicGiftQuery } from '../../../redux/api/public/gifts';
import Button from '../../../components/Button';
import { Heading3Text } from '../../../components/Texts';
import LoadingSpinner from '../../../components/LoadingSpinner';
import TextInput from '../../../components/Inputs/Text';
import { useLazyGetPromoCodeDetailsQuery } from '../../../redux/api/public/promotional';

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 100vh;
  width: 100%;
  color: ${({ theme }) => theme.text.title};
`;

const ScannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px;
  border: 2px dashed ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.background.secondary};
`;

const StatusContainer = styled.div`
  padding: 20px;
  border-radius: 8px;
  background-color: ${({ theme, status }) =>
    status === 'success'
      ? theme.status.success.background
      : status === 'error'
        ? theme.status.error.background
        : theme.background.secondary};
  border: 1px solid
    ${({ theme, status }) =>
      status === 'success'
        ? theme.status.success.primary
        : status === 'error'
          ? theme.status.error.primary
          : theme.border.primary};
`;

const StatusText = styled.p`
  color: ${({ theme, status }) =>
    status === 'success'
      ? theme.status.success.primary
      : status === 'error'
        ? theme.status.error.primary
        : theme.text.regular};
  font-weight: 600;
  margin: 0;
`;

const GiftInfo = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.text.secondary};
`;

const CheckStatus = () => {
  const [giftId, setGiftId] = useState(null);
  const [giftData, setGiftData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [manualGiftId, setManualGiftId] = useState('');
  const dispatch = useDispatch();

  const [
    getPromoCode,
    { data: giftResponse, error, isLoading: isGiftLoading },
  ] = useLazyGetPromoCodeDetailsQuery();

  const handleScanQR = async () => {
    // Check if we're on a native platform and barcode scanner is available
    if (!Capacitor.isPluginAvailable('CapacitorBarcodeScanner')) {
      toast.error('QR scanning is only available on mobile devices.');
      return;
    }

    try {
      setIsLoading(true);
      const { ScanResult } = await CapacitorBarcodeScanner.scanBarcode({
        hint: Html5QrcodeSupportedFormats.QR_CODE,
        scanInstructions: 'Point at the gift card QR code',
        scanOrientation: CapacitorBarcodeScannerScanOrientation.PORTRAIT,
        android: {
          scanningLibrary: CapacitorBarcodeScannerAndroidScanningLibrary.MLKIT,
        },
      });

      if (ScanResult) {
        // Extract gift ID from QR code
        // Assuming QR code contains a URL with gift parameter like: https://example.com/?gift=123
        let extractedGiftId = null;

        if (ScanResult.includes('?gift=')) {
          extractedGiftId = ScanResult.split('?gift=')?.[1];
        } else if (ScanResult.includes('/gift/')) {
          // Handle direct gift URL format
          const match = ScanResult.match(/\/gift\/([^/?]+)/);
          extractedGiftId = match ? match[1] : null;
        } else {
          // Assume the QR code contains just the gift ID
          extractedGiftId = ScanResult;
        }

        if (extractedGiftId) {
          setGiftId(extractedGiftId);
          await checkGiftStatus(extractedGiftId);
        } else {
          toast.error(
            'Invalid QR code format. Please scan a valid gift card QR code.',
          );
        }
      }
    } catch (error) {
      console.error('QR scan error:', error);
      toast.error('Failed to scan QR code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkGiftStatus = async (id) => {
    try {
      const result = await getPromoCode(id).unwrap();
      setGiftData(result);
      if (result.used) {
        toast.error('This gift has already been claimed');
      } else {
        toast.error(`Gift is claimable for ${result.amount} ${result.token}.`);
      }
    } catch (err) {
      console.error('Gift check error:', err);
      if (err.status === 404) {
        toast.error('Gift does not exist.');
      } else {
        toast.error('Failed to check gift status. Please try again.');
      }
      setGiftData(null);
    }
  };

  const resetScanner = () => {
    setGiftId(null);
    setGiftData(null);
    setManualGiftId('');
  };

  const handleManualCheck = () => {
    if (manualGiftId.trim()) {
      setGiftId(manualGiftId.trim());
      checkGiftStatus(manualGiftId.trim());
    } else {
      toast.error('Please enter a gift ID.');
    }
  };

  return (
    <Container>
      <Heading3Text>Gift Card QR Scanner</Heading3Text>

      <ScannerContainer>
        <Button onClick={handleScanQR} disabled={isLoading}>
          {isLoading ? (
            <LoadingSpinner />
          ) : Capacitor.isPluginAvailable('CapacitorBarcodeScanner') ? (
            'Scan QR Code'
          ) : (
            'QR Scanner (Mobile Only)'
          )}
        </Button>

        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <p style={{ margin: '10px 0', textAlign: 'center' }}>
            Or enter gift ID manually:
          </p>
          <TextInput
            value={manualGiftId}
            onChange={(e) => setManualGiftId(e.target.value)}
            placeholder='Enter gift ID'
          />
          <Button
            onClick={handleManualCheck}
            disabled={isGiftLoading || !manualGiftId.trim()}
            style={{ width: '100%' }}
          >
            {isGiftLoading ? <LoadingSpinner /> : 'Check Gift Status'}
          </Button>
        </div>

        {giftId && (
          <div>
            <p>Gift ID: {giftId}</p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
              }}
            >
              <Button
                onClick={() => checkGiftStatus(giftId)}
                disabled={isGiftLoading}
              >
                {isGiftLoading ? <LoadingSpinner /> : 'Check Status Again'}
              </Button>
              <Button onClick={resetScanner} style={{ marginLeft: '10px' }}>
                Scan New QR
              </Button>
            </div>
          </div>
        )}
      </ScannerContainer>

      {giftData && (
        <StatusContainer status={!giftData.used ? 'success' : 'error'}>
          <StatusText status={!giftData.used ? 'success' : 'error'}>
            {giftData.used ? 'Gift Already Claimed' : 'Gift Ready to Claim'}
          </StatusText>

          <GiftInfo>
            <p>
              <strong>Status:</strong>{' '}
              {giftData.used ? 'Claimed' : 'Ready to Claim'}
            </p>
            {giftData.token && (
              <p>
                <strong>Token:</strong> {giftData.token}
              </p>
            )}
            {giftData.amount && (
              <p>
                <strong>Amount:</strong> {giftData.amount}
              </p>
            )}
          </GiftInfo>
        </StatusContainer>
      )}
    </Container>
  );
};

export default CheckStatus;
